import React from 'react';
import { buildURL, SharedImigixAndSourceProps } from 'react-imgix';

import { useConfig } from 'ha/helpers/config';
import { LEGACY_URL_RE, BOSCH_URL_RE, IMGIX_URL_RE } from './constants';
import { isContentfulImage, isLocalImage } from './imageSource';

const OWNER_TYPE_MAP = {
  listing: 'room',
  user: 'hauser',
  unilogo: 'uni_logo',
  uniphoto: 'uni_photo',
};

const ROTATION_VALUES = ['0', '90', '180', '270'];

type TransformImageURL = (
  src: string,
  params?: SharedImigixAndSourceProps['imgixParams'],
) => string;

interface UseImgixResponse {
  transformImageURL: TransformImageURL;
}

export const useImgix = (): UseImgixResponse => {
  const { imgix } = useConfig();

  const transformImageURL: TransformImageURL = React.useCallback(
    (src, params) => {
      if (isLocalImage(src) || isContentfulImage(src)) {
        return src;
      }

      const parsedUrl = new URL(src);

      const path = parsedUrl.pathname.replace('/v2', '').replace('.jpg', '');

      let orientation: string | undefined;

      let urlSearchParams: URLSearchParams | undefined;

      if (parsedUrl.search && typeof URLSearchParams !== 'undefined') {
        urlSearchParams =
          // @ts-ignore
          parsedUrl.searchParams || new URLSearchParams(parsedUrl.search);
      }

      function updateRotationFromSearchParams(key: string) {
        if (urlSearchParams) {
          if (!orientation && urlSearchParams.has(key)) {
            const value = urlSearchParams.get(key);

            orientation =
              value && value !== '0' && ROTATION_VALUES.includes(value)
                ? value
                : undefined;
          }
        }
      }
      let ownerType: string;
      let ownerId: string;
      let imageId: string;

      if (LEGACY_URL_RE.test(src)) {
        [, , ownerType, ownerId, imageId] = path.split('/');

        updateRotationFromSearchParams('rotate');
      } else if (BOSCH_URL_RE.test(src)) {
        [, , ownerType, ownerId, , imageId] = path.split('/');

        ownerType = OWNER_TYPE_MAP[ownerType];

        updateRotationFromSearchParams('r');
      } else if (IMGIX_URL_RE.test(src)) {
        [, ownerType, ownerId, imageId] = path.split('/');

        updateRotationFromSearchParams('orient');
      } else {
        return src;
      }

      const baseUrl = `${imgix.sourceURL}/${ownerType}/${ownerId}/${imageId}.jpg`;

      return buildURL(
        baseUrl,
        orientation ? { ...params, orient: orientation } : params,
      );
    },
    [imgix],
  );

  const result = React.useMemo(
    () => ({ transformImageURL }),
    [transformImageURL],
  );

  return result;
};
